import {HttpClient} from '@angular/common/http';
import {inject, Injectable, InjectionToken} from '@angular/core';
import {Observable, timeout} from 'rxjs';
import {ConnectApiService} from './connect-api.service';

export interface SyntheticTransferConfiguration {
    recordCount: number;
    multiplier: number;
    failAtRecord: number;
}

export interface IntegrationSummary {
    id: string;
    technicalKey: string;
    uiKey: string;
    uiKind?: 'native-angular' | 'shine.sandboxed-ui.v1';
    name: string;
    draftRevision: number;
    activeRevision: number | null;
    running: boolean;
    validationMode?: 'inline' | 'async';
    capabilities?: {read: boolean; configure: boolean; run: boolean};
    runtimeAvailable?: boolean;
    runtimeProfile?: string;
    preview?: {kind: 'synthetic-fixture'};
    application?: CodedApplication;
}

export interface IntegrationList {
    items: IntegrationSummary[];
    capabilities?: {installationAvailable: boolean};
}

export interface IntegrationConfigurationRevision<T = SyntheticTransferConfiguration> {
    revision: number;
    configuration: T;
}

export interface IntegrationDetail<T = SyntheticTransferConfiguration> extends IntegrationSummary {
    draft: IntegrationConfigurationRevision<T>;
    active: IntegrationConfigurationRevision<T> | null;
    latestValidation?: IntegrationValidationOperation | null;
}

export interface IntegrationValidationOperation {
    id: string;
    integrationId: string;
    status: 'queued' | 'running' | 'succeeded' | 'failed' | 'stopped';
    configurationRevision: number;
    packageDigest?: string;
    valid: boolean | null;
    errors: IntegrationValidationError[];
    errorCode?: string | null;
}

export interface IntegrationValidationError {
    field: string;
    code: string;
    message: string;
}

export interface IntegrationValidation {
    valid: boolean;
    revision: number;
    errors: IntegrationValidationError[];
}

export interface IntegrationRun<T = SyntheticTransferConfiguration> {
    id: string;
    integrationId: string;
    status: 'queued' | 'running' | 'succeeded' | 'failed' | 'stopped';
    configurationRevision: number;
    packageDigest?: string;
    configuration: T;
    createdAt: string;
    scheduledFor?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
    processed: number;
    created: number;
    updated?: number;
    skipped: number;
    outputDigest?: string | null;
    errorCode?: string | null;
    healthSummary?: HealthSummary | null;
    kind?: string;
    trigger?: {type: 'manual' | 'schedule' | 'webhook' | 'continuous'; eventId?: string};
    result?: CodedResult | null;
    stopRequested?: boolean;
}

export interface CodedResult {
    contractVersion: 'coded-integration.v1';
    // `stopped` is API-owned control metadata, never an application worker result.
    status: 'succeeded' | 'failed' | 'stopped';
    counts?: {processed: number; created: number; updated: number; skipped: number};
    report?: Record<string, unknown>;
    preview?: Record<string, unknown>;
    outputDigest?: string;
    errorCode?: string;
}
export interface CodedConnection {
    alias: string;
    authentication: 'oauth2' | 'apiKey' | 'workloadIdentity';
    access: 'read' | 'write';
    providerRef: string;
    requiredScopes: string[];
    fields: {key: string; label: string; secret: boolean; helpUrl: string}[];
    connectionId: string | null;
    deploymentManaged: boolean;
}
export interface CodedContinuousState {
    revision: number;
    enabled: boolean;
    generation: string | null;
    windowExpiresAt: string | null;
    packageDigest: string | null;
    configurationRevision: number | null;
    sequence: number;
    lastOperationId: string | null;
    notBefore: string | null;
    failureCount: number;
    restartCount: number;
    status: 'disabled' | 'waiting' | 'running' | 'backoff' | 'needs_attention' | 'window_closed' |
        'configuration_changed' | 'package_changed' | 'paused';
    errorCode: string | null;
}
export interface CodedApplication {
    packageVersion: string;
    installedDigest: string;
    availableDigest: string;
    upgradeRequired: boolean;
    paused: boolean;
    controlRevision: number;
    operations: ('validate' | 'preview' | 'run' | 'serve')[];
    triggers: ('manual' | 'schedule' | 'webhook')[];
    checkpointRevision: number;
    stateVersion: number;
    effectCount: number;
    reconciliationRequired: number;
    pendingWebhooks: number;
    webhooks?: {key: string; path: string; authentication: 'hmac-sha256'; timestampToleranceSeconds: number}[];
    packageHistory: {digest: string; version: string; activatedAt: string}[];
    latestResult: CodedResult | null;
    latestExecution?: {id: string; status: 'succeeded' | 'failed' | 'stopped'; stopRequested: boolean; errorCode?: string | null} | null;
    continuous?: CodedContinuousState;
    connections: CodedConnection[];
}
export interface CodedIntegrationDetail extends IntegrationDetail<object> {
    application: CodedApplication;
    schedule: IntegrationSchedule;
    capacity: {remainingOperations: number; maxOperations: number; remainingStateBytes: number};
}

export interface ScheduledJobHealthConfiguration {
    maxSnapshotAgeSeconds: number;
    failureDedupeMinutes: number;
    staleLockDedupeMinutes: number;
}
export interface HealthCondition {
    jobKey: string;
    condition: 'scheduled_job_failed' | 'scheduled_job_stale_lock';
    observedAt: string;
    windowStart: string;
    occurrences: number;
}
export interface HealthSummary {
    health: 'clear' | 'attention';
    sourceGeneratedAt: string;
    checkedAt: string;
    jobCount: number;
    failedJobs: number;
    expiredLocks: number;
}
export interface HealthReport extends HealthSummary {
    contractVersion: 'scheduled-job-health.v1';
    status: 'succeeded';
    conditions: HealthCondition[];
    outputDigest: string;
}
export interface ConditionRollup {
    jobKey: string;
    condition: HealthCondition['condition'];
    windowStart: string;
    firstSeenAt: string;
    lastSeenAt: string;
    occurrences: number;
    observations: number;
}
export interface IntegrationSchedule {
    revision: number;
    enabled: boolean;
    intervalMinutes: number;
    nextDueAt: string | null;
    lastOutcome: string | null;
}
export interface HealthIntegrationDetail extends IntegrationDetail<ScheduledJobHealthConfiguration> {
    latestReport: HealthReport | null;
    reportState: {stale: boolean; lastCheckAt: string | null; errorCode: string | null};
    conditionRollups: ConditionRollup[];
    schedule: IntegrationSchedule;
    capacity: {remainingOperations: number; maxOperations: number; remainingStateBytes: number; remainingConditionRollups: number};
}

type IntegrationTransport = Pick<HttpClient, 'get' | 'post' | 'put'>;

export const CONNECT_INTEGRATIONS_TRANSPORT = new InjectionToken<IntegrationTransport>('Connect integrations transport', {
    providedIn: 'root',
    factory: () => inject(ConnectApiService) as IntegrationTransport,
});

@Injectable({providedIn: 'root'})
export class ConnectIntegrationsService {
    private readonly api = inject(CONNECT_INTEGRATIONS_TRANSPORT);
    private readonly path = '/api/v1/integrations';

    list(): Observable<IntegrationList> {
        return this.api.get<IntegrationList>(this.path).pipe(timeout(15000));
    }

    get<T = IntegrationDetail>(id: string): Observable<T> {
        return this.api.get<T>(this.integrationPath(id)).pipe(timeout(15000));
    }

    saveDraft<T = IntegrationDetail>(id: string, expectedRevision: number, configuration: object): Observable<T> {
        return this.api.put<T>(`${this.integrationPath(id)}/draft`, {expectedRevision, configuration}).pipe(timeout(15000));
    }

    validate(id: string, expectedRevision: number): Observable<IntegrationValidation> {
        return this.api.post<IntegrationValidation>(`${this.integrationPath(id)}/validate`, {expectedRevision}).pipe(timeout(60000));
    }

    validateHosted(id: string, expectedRevision: number, idempotencyKey: string): Observable<{validation: IntegrationValidationOperation}> {
        return this.api.post<{validation: IntegrationValidationOperation}>(`${this.integrationPath(id)}/validate`, {expectedRevision, idempotencyKey}).pipe(timeout(15000));
    }

    getValidation(id: string, operationId: string): Observable<{validation: IntegrationValidationOperation}> {
        return this.api.get<{validation: IntegrationValidationOperation}>(`${this.integrationPath(id)}/validations/${encodeURIComponent(operationId)}`).pipe(timeout(15000));
    }

    activate<T = IntegrationDetail>(id: string, expectedRevision: number): Observable<T> {
        return this.api.post<T>(`${this.integrationPath(id)}/activate`, {expectedRevision}).pipe(timeout(60000));
    }

    runOnce<T = SyntheticTransferConfiguration>(id: string, expectedActiveRevision: number, idempotencyKey: string): Observable<{run: IntegrationRun<T>}> {
        return this.api.post<{run: IntegrationRun<T>}>(`${this.integrationPath(id)}/runs`, {expectedActiveRevision, idempotencyKey}).pipe(timeout(15000));
    }

    listRuns<T = SyntheticTransferConfiguration>(id: string): Observable<{items: IntegrationRun<T>[]; nextSkip?: number | null}> {
        return this.api.get<{items: IntegrationRun<T>[]; nextSkip?: number | null}>(`${this.integrationPath(id)}/runs`).pipe(timeout(15000));
    }

    getRun(id: string, runId: string): Observable<{run: IntegrationRun}> {
        return this.api.get<{run: IntegrationRun}>(`${this.integrationPath(id)}/runs/${encodeURIComponent(runId)}`).pipe(timeout(15000));
    }

    saveSchedule(id: string, expectedRevision: number, enabled: boolean, intervalMinutes: number): Observable<HealthIntegrationDetail> {
        return this.api.put<HealthIntegrationDetail>(`${this.integrationPath(id)}/schedule`, {expectedRevision, enabled, intervalMinutes}).pipe(timeout(15000));
    }

    executeCoded(id: string, expectedActiveRevision: number, idempotencyKey: string, operation: 'preview' | 'run' | 'serve'): Observable<{operation: IntegrationRun<object>}> {
        return this.api.post<{operation: IntegrationRun<object>}>(`${this.integrationPath(id)}/executions/${operation}`,
            {expectedActiveRevision, idempotencyKey, mode: operation === 'serve' ? 'continuous' : 'batch'}).pipe(timeout(15000));
    }

    codedHistory(id: string, operation: 'preview' | 'run' | 'serve'): Observable<{items: IntegrationRun<object>[]; nextSkip?: number | null}> {
        return this.api.get<{items: IntegrationRun<object>[]; nextSkip?: number | null}>(`${this.integrationPath(id)}/executions/${operation}`).pipe(timeout(15000));
    }

    controlCoded(id: string, expectedRevision: number, paused: boolean): Observable<CodedIntegrationDetail> {
        return this.api.put<CodedIntegrationDetail>(`${this.integrationPath(id)}/control`, {expectedRevision, paused}).pipe(timeout(15000));
    }

    setCodedContinuous(id: string, expectedRevision: number, expectedActiveRevision: number, enabled: boolean, idempotencyKey: string): Observable<CodedIntegrationDetail> {
        return this.api.put<CodedIntegrationDetail>(`${this.integrationPath(id)}/continuous`,
            {expectedRevision, expectedActiveRevision, enabled, idempotencyKey}).pipe(timeout(15000));
    }

    upgradeCoded(id: string, expectedPackageDigest: string, expectedCheckpointRevision: number, migratedCheckpoint?: object): Observable<CodedIntegrationDetail> {
        return this.api.post<CodedIntegrationDetail>(`${this.integrationPath(id)}/upgrade`, {expectedPackageDigest, expectedCheckpointRevision,
            ...(migratedCheckpoint ? {migratedCheckpoint} : {})}).pipe(timeout(15000));
    }

    bindCodedConnection(id: string, expectedRevision: number, alias: string, connectionId: string | null): Observable<CodedIntegrationDetail> {
        return this.api.put<CodedIntegrationDetail>(`${this.integrationPath(id)}/connection-bindings`, {expectedRevision, alias, connectionId}).pipe(timeout(15000));
    }

    codedState<T extends object>(id: string): Observable<{checkpoint: {revision: number; version: number; data: T}}> {
        return this.api.get<{checkpoint: {revision: number; version: number; data: T}}>(`${this.integrationPath(id)}/state`).pipe(timeout(15000));
    }

    private integrationPath(id: string): string {
        return `${this.path}/${encodeURIComponent(id)}`;
    }
}
